<template>
  <b-card
    v-if="record && canChangeStatus && !completed"
    class="RealisationStatusPanel"
  >
    <h4>Status wijzigen</h4>

    <p v-if="cancelled">
      Het proces is momenteel geannuleerd
    </p>
    <p v-if="onhold">
      Het proces staat momenteel on hold
    </p>
    <p v-if="locationUnavailable">
      {{ locationUnavailableMessage }}
    </p>

    <div class="d-flex justify-content-between align-items-end">
      <b-button
        :disabled="busy || cancelled"
        :variant="action === 'onhold' ? 'warning' : 'outline-warning'"
        class="mr-2"
        @click="setAction({ action: onhold ? 'resume' : 'onhold' })"
      >
        {{ onhold ? 'Hervat' : 'Zet on hold' }}
      </b-button>
      <b-button
        :disabled="busy || locationUnavailable"
        :variant="action === 'cancel' ? 'danger' : 'outline-danger'"
        @click="setAction({ action: cancelled ? 'restore' : 'cancel' })"
      >
        {{ cancelled ? 'Herstel' : 'Annuleer' }}
      </b-button>
    </div>

    <Feedback
      class="mt-2"
      :feedback="feedback"
    />

    <transition>
      <div v-if="action">
        <div v-if="['onhold', 'cancel'].includes(action)">
          <b-select
            v-model="reason"
            :disabled="busy"
            :options="reasonOptions"
            class="mt-2"
          />
          <div v-if="action && action !== 'restore'" class="pt-1">Toelichting</div>
          <b-textarea
            v-model="comment"
            :disabled="busy"
            class="mt-2"
          />
        </div>
        <b-button
          :disabled="busy"
          variant="dark"
          class="w-100 mt-2"
          @click="handleConfirm"
        >
          {{ actionConfirmLabel }}
        </b-button>
      </div>
    </transition>
  </b-card>
</template>

<script>

import { mapGetters, mapMutations } from 'vuex'

import Feedback from '@/components/form/Feedback.vue'

import { onHoldReasons, cancelReasons } from '@/config'
import { multilineUnicodeString, getInvalidCharacters } from '@/services/validation'

export default {
  components: {
    Feedback,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      record: null,
      busy: false,
      location: null,

      action: null,
      actionReason: null,
      comment: '',

      onHoldReasons,
      cancelReasons,
      feedback: {
        show: false,
        message: '',
      },
    }
  },
  computed: {
    ...mapGetters('realisation', [
      'recordByUuid',
    ]),
    ...mapGetters('user', [
      'canChangeStatus',
    ]),
    ...mapGetters('chargingpoints', [
      'getChargingPointByUUID',
    ]),
    ...mapGetters('tenant', [
      'isCurrentTenant',
    ]),
    locationUnavailable() {
      try {
        // This check is only relevant for GO-RAL
        if (! this.isCurrentTenant({ tenant: 'go-ral' })) {
          return false
        }
        if (! this.cancelled) {
          return false
        }

        const uuid = this.record.cbapi 
          ? this.record.getConnectedLocationUuid()
          : this.record.CurrentLocation.Location.chargingpointUuid

        console.log("LOCATION UNAVAILABLE 0", uuid, this.record.cbapi)
        
        if (! uuid) {
          return false
        }

        let location = this.getChargingPointByUUID({
          uuid
        })

        console.log("LOCATION UNAVAILABLE 1", location)

        // If the location is connected to this RP, the location is available to this RP
        if (location.getWorkflowUuid() === this.uuid) {
          return false
        }

        console.log("LOCATION UNAVAILABLE 2", location)

        return location.getStatus() !== 'definitive'
      } catch (e) {
        return true
      }
    },

    completed() {
      try {
        return !! this.record.isCompleted()
      } catch (e) {
        return false
      }
    },
    onhold() {
      try {
        return !! this.record.isOnHold()
      } catch (e) {
        return false
      }
    },
    cancelled() {
      try {
        return !! this.record.isCancelled()
      } catch (e) {
        return false
      }
    },

    cancelReason() {
      try {
        return this.actionReason 
          ? this.actionReason 
          : this.record.getStatusMeta({ name: 'cancelled' })?.reason
      } catch (e) {
        return null
      }
    },
    onholdReason() {
      try {
        return this.actionReason 
          ? this.actionReason 
          : this.record.getStatusMeta({ name: 'onhold' })?.reason
      } catch (e) {
        return null
      }
    },

    reason: {
      get() {
        return this.action === 'cancel' ? this.cancelReason : this.onholdReason
      },
      set(value) {
        this.actionReason = value
      },
    },
    reasonOptions() {
      return this.action === 'cancel' ? this.cancelReasons : this.onHoldReasons
    },

    actionConfirmLabel() {
      switch (true) {
        case this.action === 'restore':
          return 'Herstel het proces'
        case this.action === 'cancel':
          return 'Annuleer het proces'
        case this.action === 'resume':
          return 'Hervat het proces'
        case this.action === 'onhold':
          return 'Zet on hold'
      }

      return 'Bevestigen'
    },
    locationUnavailableMessage () {
      const rejected = this.location.data.properties.status === 'rejected'
      return rejected
        ? 'Herstel is niet meer mogelijk omdat de locatie inmiddels is afgekeurd'
        : 'Herstel is niet meer mogelijk omdat de locatie inmiddels aan een ander proces is gekoppeld.'
    },
  },
  watch: {
    action: {
    immediate: true,
    handler () {
      this.reason = ''
      this.comment = ''
    },
   }
  },
  created() {
    this.record = this.recordByUuid({
      uuid: this.uuid
    })
  },
  methods: {
    ...mapMutations('realisation', [
      'replaceRecord',
    ]),
    ...mapMutations('chargingpoints', [
      'replaceLocation',
    ]),
    setAction({ action }) {
      this.action = this.action === action ? null : action
    },
    handleConfirm: async function() {
      try {
        
        if (this.record.isUnsavedRecord()) return
        
        this.busy = true
        this.feedback = {
          show: false,
          message: '',
        }

        // Validation of reason & comment when relevant (e.g. action = onhold || cancel)
        if (['onhold', 'cancel'].includes(this.action)) {
          if (! this.reason) {
            this.feedback = {
              show: true,
              variant: 'danger',
              message: 'Er moet een reden geselecteerd worden',
            }
            this.busy = false
            return
          }

          if (
            (this.action === 'onhold' && ! this.onHoldReasons.map(reason => reason.value).includes(this.reason))
            || (this.action === 'cancel' && ! this.cancelReasons.map(reason => reason.value).includes(this.reason))
          ){
            this.feedback = {
              show: true,
              variant: 'danger',
              message: 'Er moet een geldige reden geselecteerd worden',
            }
            this.busy = false
            return
          }

          if (this.comment !== '') {
            if (this.comment.length > 300) {
              this.feedback = {
                show: true,
                variant: 'danger',
                message: 'De reden omschrijving mag maximaal 300 karakters lang zijn',
              }
              this.busy = false
              return
            }
            if (! multilineUnicodeString(this.comment)) {
              this.feedback = {
                show: true,
                variant: 'danger',
                message: `De reden omschrijving bevat karakters welke om veiligheidsredenen niet geaccepteerd worden: ${getInvalidCharacters(this.comment)}`,
              }
              this.busy = false
              return
            }
          }
        }

        /**
         * Map old data format to CBAPI format
         *  "restore" => disabled cancel
         *  "cancel" => enable cancel
         *  "resume" => disable onhold
         *  "onhold" => enable onhold
         * 
         *  So "restore" / "cancel" used to refer to the "cancelled" state
         *  and "restore" / "resume" used to refer to disabling a state
         */ 
        let status = ['restore', 'cancel'].includes(this.action) ? 'cancelled' : 'onhold'
        let action = ['restore', 'resume'].includes(this.action) ? 'disable' : 'enable'


        /**
         * Refresh the location record if we're changing the cancelled state, 
         *  and the RP has a connected location record
         */
        let refreshLocationUuid = this.status === 'cancelled'
          ? this.record.getConnectedLocation({ category: 'selected' })
          : null


        // Make the call
        let response = await this.$cbapi.process.setStatus({
          uuid: this.record.getUuid(),
          status,
          synced: this.record.getSyncedTS(), 
          action,
          reason: this.reason,
          comment: this.comment,
        })

        if (response === false) {
          throw new Error('Unable to update the status')
        }
        
        this.feedback = {
          show: true,
          variant: 'success',
          message: 'De status wijziging is opgeslagen',
        }

        // Update RP record
        this.replaceRecord({
          record: response?.data,
        })

        console.log("REFRESH LOCATION", refreshLocationUuid)

        // Get fresh location, if relevant
        if (refreshLocationUuid) {
          response = await this.$cbapi.locations.getByUuid({
            uuid: refreshLocationUuid
          })

          if (response === false) {
            this.feedback = {
              show: true,
              variant: 'danger',
              message: 'De status wijziging is opgeslagen, maar het verversen van het record is mislukt',
            }
            throw new Error("something went wrong")
          }

          this.replaceLocation({
            location: response?.data
          })
        }

        //
        this.action = null
        this.busy = false
        this.$emit('update-component')
      } catch (e) {
        console.log(e)

        this.busy = false

        this.feedback = {
          show: true,
          variant: 'danger',
          message: this.feedback.message || 'De status wijziging is niet opgeslagen',
        }
      }
    },
  },
}
</script>

<style lang="scss">
.RealisationStatusPanel {
  h4, p {
    text-align: center;
  }
}
</style>
